import * as React from 'react';
import { FC, useEffect } from 'react';
import useBalance from 'app/useBalance';
import { isBlank } from './utils';
import PrependFields from './PrependFields';
import AppendFields from './AppendFields';
import ProfileFields from './ProfileFields';
import SubmitButton from './SubmitButton';
import Twofa from './Twofa';
import Error from './Error';
import MainFields from './MainFields';
import TransactionLimits from './TransactionLimits';
import LimitsUsage from './LimitsUsage';
import BalanceUsage from './BalanceUsage';
import Result from './Result';
import ResultCalculation from './ResultCalculation';
import useFormContext, { PaymentFormProps, FormCtx } from './useFormContext';
import useForm from './useForm';
import useValidationState from './useValidationState';
import useMainFields from './useMainFields';
import useAmountDetails from './useAmountDetails';
import useTransactionLimits from './useTransactionLimits';
import useAmountValidation from './useAmountValidation';
import useAmountValidationStateUpdate from './useAmountValidationStateUpdate';
import useLimitStats from './useLimitStats';

const PaymentForm: FC<PaymentFormProps> = (props) => {
  const [vendorCurrency, changeVendorCurrency, formAmount, changeFormAmount] = useMainFields(
    props.selected_currency || ''
  );
  const context = useFormContext(props, vendorCurrency, changeVendorCurrency);
  const [isAvailable, handleSubmit, isSubmitted, error] = useForm(context);

  const [updateValidationState, isValid] = useValidationState();
  const [amount, vendorAmount, formCurrency, resultSum, creditedCurrency] = useAmountDetails(
    context,
    vendorCurrency,
    formAmount
  );
  const [minTnx, maxTnx] = useTransactionLimits(context, vendorCurrency);
  const balance = +useBalance({ balance: context.balance, bonus: 0 }).balance;
  const [isMinTnxViolated, isMaxTnxViolated, isBalanceViolated, isAmountInLimits, isAmountValid] = useAmountValidation(
    context,
    balance,
    amount,
    vendorAmount,
    minTnx,
    maxTnx
  );
  useAmountValidationStateUpdate(vendorCurrency, formAmount, isAmountValid, updateValidationState);
  const [effectivePaysystemLimitStats, effectivePlayerLimitStats, hasLimits] = useLimitStats(
    context,
    amount,
    vendorAmount,
    vendorCurrency
  );

  useEffect(() => {
    const formSpinner = document.getElementById('js-form-spinner');
    if (formSpinner) {
      formSpinner.style.display = 'none';
    }
  }, []);

  if (!isAvailable) {
    return <div className="alert alert-error rs-text-align_center">{context.locales.payment_method_not_available}</div>;
  }

  if (!vendorCurrency) return null;

  return (
    <FormCtx.Provider value={context}>
      <form
        // These form parameters are necessary for rspec tests
        action={context.url}
        method="post"
        autoComplete="off"
        acceptCharset="UTF-8"
        className="payment-form"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="utf8" value="✓" />
        <input type="hidden" name="paysystem" value={context.paysystem} />
        <input type="hidden" name="payment_provider" value={context.paymentProvider} />

        <Error error={error} />

        <div className="rs-flex">
          <div className="rs-flex-item rs-large-7 fields-area">
            <PrependFields updateValidationState={updateValidationState} />
            <MainFields
              currency={vendorCurrency}
              changeCurrency={changeVendorCurrency}
              amount={formAmount}
              changeAmount={changeFormAmount}
              amountCurrency={formCurrency}
              hasAmountError={!isBlank(formAmount) && !isAmountValid}
            />
            <AppendFields vendorCurrency={vendorCurrency} updateValidationState={updateValidationState} />
            <ProfileFields fields={props.profile_fields} updateValidationState={updateValidationState} />
            <Twofa updateValidationState={updateValidationState} />
            <br />
            <SubmitButton isSubmitted={isSubmitted} isValid={isValid} />
          </div>

          <div className="rs-flex-item rs-large-5 limits-area">
            {hasLimits && (
              <>
                <TransactionLimits
                  minTnx={minTnx}
                  isMinTnxViolated={isMinTnxViolated}
                  maxTnx={maxTnx}
                  isTaxTnxViolated={isMaxTnxViolated}
                  vendorCurrency={vendorCurrency}
                />
                <LimitsUsage
                  effectivePaysystemLimitStats={effectivePaysystemLimitStats}
                  effectivePlayerLimitStats={effectivePlayerLimitStats}
                />
              </>
            )}
            <BalanceUsage balance={balance} isBalanceViolated={isBalanceViolated} />
            <Result isAmountInLimits={isAmountInLimits} resultSum={resultSum} creditedCurrency={creditedCurrency} />
            <ResultCalculation
              formAmount={formAmount}
              formCurrency={formCurrency}
              resultSum={resultSum}
              creditedCurrency={creditedCurrency}
              vendorCurrency={vendorCurrency}
            />
          </div>
        </div>
      </form>
    </FormCtx.Provider>
  );
};

export default PaymentForm;
